// @ts-nocheck
import { Dialog, Transition } from '@headlessui/react';
import { ExclamationTriangleIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { URL } from '../../service/Service';
import RateModal from './RateModal';
import RateModalTax from './RateModalTax';
import mbWayLogo from './mbw.png';
import multibancoLogo from './mb.png';
import creditCardLogo from './ccc.png';
import directDebitLogo from './cc.png';
import paysafecardLogo from './paysafecard.png';
import paysafecashLogo from './paysafecash.png';
import santanderLogo from './santander.png';

const serviceMeta = {
  PS: { label: 'Payshop', shortLabel: 'PS' },
  MB: { label: 'Multibanco', image: multibancoLogo, shortLabel: 'MB' },
  MBW: { label: 'MB Way', image: mbWayLogo, shortLabel: 'MB' },
  CC: { label: 'Cartão de Crédito', image: creditCardLogo, shortLabel: 'CC' },
  TPA: { label: 'TPA', shortLabel: 'TPA' },
  DD: { label: 'Débito Direto', image: directDebitLogo, shortLabel: 'DD' },
  BZ: { label: 'Bizum', shortLabel: 'BZ' },
  PF: { label: 'Paysafecard', image: paysafecardLogo, shortLabel: 'PF' },
  SC: { label: 'Santander', image: santanderLogo, shortLabel: 'SC' },
  SPMB: { label: 'Split Payment (Multibanco)', image: multibancoLogo, shortLabel: 'SP', badge: 'Split' },
  SPMBW: { label: 'Split Payment (MB Way)', image: mbWayLogo, shortLabel: 'SP', badge: 'Split' },
  PSC: { label: 'Paysafecash', image: paysafecashLogo, shortLabel: 'PSC' },
};

const ServiceLogo = ({ nameService, size = 'normal' }) => {
  const service = serviceMeta[nameService] || { label: nameService, shortLabel: nameService };
  const sizeClass = size === 'small' ? 'h-12 w-14 rounded-xl' : 'h-14 w-16 rounded-2xl';

  return (
      <span className={`relative flex shrink-0 items-center justify-center overflow-hidden border border-gray-100 bg-white shadow-sm ${sizeClass}`}>
        {service.image ? (
            <img src={service.image} alt={service.label} className="h-full w-full object-contain p-2" />
        ) : (
            <span className="text-xs font-black tracking-tight text-green-cl">
              {service.shortLabel || '--'}
            </span>
        )}
        {service.badge && (
            <span className="absolute bottom-0 right-0 rounded-tl-md bg-green-cl px-1.5 py-0.5 text-[8px] font-bold uppercase leading-none text-white">
              {service.badge}
            </span>
        )}
      </span>
  );
};

const getRateTypeLabel = (rateType) => {
  if (rateType === 'FIXED_RATE') return 'Taxa fixa';
  if (rateType === 'VARIABLE_RATE') return 'Taxa variável';
  if (rateType === 'MIXED_RATE') return 'Taxa mista (fixa e variável)';
  return rateType;
};

const RateValue = ({ rate }) => (
    <div className="mt-2 flex items-center text-2xl font-bold text-green-900">
      {rate.rateType === 'FIXED_RATE' && (
          <>
            {Number(rate.rateValue).toLocaleString('pt-PT', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            <span className="ml-0.5">€</span>
          </>
      )}
      {rate.rateType === 'VARIABLE_RATE' && <>{rate.ratePercentage}%</>}
      {rate.rateType === 'MIXED_RATE' && (
          <>
            {Number(rate.rateValue).toLocaleString('pt-PT', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            <span className="ml-0.5">€</span>
            <span className="mx-1.5 text-gray-400">+</span>
            {rate.ratePercentage}%
          </>
      )}
    </div>
);

const ConfirmationModal = ({ open, rate, deleting, deleteError, onDelete, onCancel }) => {
  const cancelButtonRef = useRef(null);
  const service = rate ? serviceMeta[rate.nameService] || { label: rate.nameService } : null;

  return (
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-[60]" initialFocus={cancelButtonRef} onClose={deleting ? () => {} : onCancel}>
          <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-950/50 backdrop-blur-[2px]" />
          </Transition.Child>

          <div className="fixed inset-0 z-[60] overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
              <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-200"
                  enterFrom="opacity-0 translate-y-3 scale-95"
                  enterTo="opacity-100 translate-y-0 scale-100"
                  leave="ease-in duration-150"
                  leaveFrom="opacity-100 translate-y-0 scale-100"
                  leaveTo="opacity-0 translate-y-3 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg overflow-hidden rounded-3xl border border-white/70 bg-white shadow-2xl">
                  <div className="flex items-start justify-between px-6 pb-4 pt-6 sm:px-7 sm:pt-7">
                    <div className="flex items-start gap-4">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-red-50 text-red-600 ring-1 ring-red-100">
                        <ExclamationTriangleIcon className="h-6 w-6" />
                      </span>
                      <div>
                        <Dialog.Title as="h3" className="text-xl font-extrabold tracking-tight text-gray-950">
                          Apagar taxa?
                        </Dialog.Title>
                        <p className="mt-1 text-sm leading-6 text-gray-500">
                          Esta ação é permanente e não pode ser anulada.
                        </p>
                      </div>
                    </div>

                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={deleting}
                        className="ml-4 rounded-xl p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label="Fechar"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>

                  {rate && (
                      <div className="px-6 sm:px-7">
                        <div className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-gray-50/80 p-4">
                          <ServiceLogo nameService={rate.nameService} size="small" />
                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-400">
                              Método de pagamento
                            </p>
                            <p className="mt-1 truncate text-base font-extrabold text-gray-900">
                              {service?.label}
                            </p>
                            <p className="mt-0.5 text-sm text-gray-500">{getRateTypeLabel(rate.rateType)}</p>
                          </div>
                        </div>

                        <p className="mt-4 text-sm leading-6 text-gray-600">
                          Para voltar a utilizar este método de pagamento terá de criar uma nova taxa.
                        </p>

                        {deleteError && (
                            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                              {deleteError}
                            </div>
                        )}
                      </div>
                  )}

                  <div className="mt-6 flex flex-col-reverse gap-3 border-t border-gray-100 bg-gray-50/60 px-6 py-5 sm:flex-row sm:justify-end sm:px-7">
                    <button
                        ref={cancelButtonRef}
                        type="button"
                        onClick={onCancel}
                        disabled={deleting}
                        className="inline-flex min-h-[46px] items-center justify-center rounded-xl border border-gray-200 bg-white px-5 text-sm font-bold text-gray-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={() => onDelete(rate)}
                        disabled={deleting}
                        className="inline-flex min-h-[46px] items-center justify-center gap-2 rounded-xl bg-red-600 px-5 text-sm font-bold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-red-700 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-red-200 disabled:cursor-not-allowed disabled:translate-y-0 disabled:opacity-60"
                    >
                      <TrashIcon className="h-5 w-5" />
                      {deleting ? 'A apagar...' : 'Apagar taxa'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
  );
};

const NewRates = () => {
  const [rates, setRates] = useState([]);
  const [error, setError] = useState(null);
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedRate, setSelectedRate] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const getPartnerRates = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError(null);

    try {
      const jwtToken = sessionStorage.getItem('jwt');
      const res = await axios.get(`${URL}/api/partners/${id}/rates`, {
        headers: {
          Authorization: `Bearer ${jwtToken}`,
        },
      });

      setRates(res.data);
    } catch (requestError) {
      console.error(requestError);
      setError(requestError.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    getPartnerRates();
  }, [getPartnerRates]);

  const handleRateSaved = async (updatedRates) => {
    // O POST /rate devolve a lista completa e já atualizada. Atualizamos o estado
    // imediatamente com essa resposta, sem depender de um segundo pedido nem de F5.
    if (Array.isArray(updatedRates)) {
      setRates(updatedRates);
      setError(null);
      return;
    }

    // Fallback defensivo caso o contrato da API mude no futuro.
    await getPartnerRates({ silent: true });
  };

  const handleDelete = (rate) => {
    setSelectedRate(rate);
    setDeleteError('');
    setShowModal(true);
  };

  const handleCancelDelete = () => {
    if (deleting) return;
    setShowModal(false);
    setSelectedRate(null);
    setDeleteError('');
  };

  const handleConfirmDelete = async (rate) => {
    if (!rate || deleting) return;

    setDeleting(true);
    setDeleteError('');

    try {
      const jwtToken = sessionStorage.getItem('jwt');
      await axios.delete(
          `${URL}/api/partners/${id}/rate/${rate.nameService}`,
          {
            headers: {
              Authorization: `Bearer ${jwtToken}`,
            },
          }
      );

      setRates((prevRates) =>
          prevRates.filter((currentRate) => currentRate.nameService !== rate.nameService)
      );
      setShowModal(false);
      setSelectedRate(null);
    } catch (requestError) {
      console.error(requestError);
      setDeleteError('Não foi possível apagar a taxa. Volte a tentar.');
    } finally {
      setDeleting(false);
    }
  };

  return (
      <div className="mb-10 min-w-0 overflow-hidden rounded-3xl bg-white shadow-xl ring-1 ring-gray-100">
        {loading && <p className="px-6 py-4 text-sm text-gray-500">A carregar taxas...</p>}
        {error && <p role="alert" className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">Não foi possível carregar as taxas. Volte a tentar.</p>}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-green-cl">Comissões do parceiro</p>
            <h1 className="mt-1 text-2xl font-black tracking-tight text-gray-950 sm:text-3xl">
              Taxas atualmente ativas
            </h1>
            <p className="mt-1 max-w-3xl text-sm text-gray-500">
              Verifica as taxas atualmente ativas, podendo também criar ou
              alterá-las se já estiverem criadas.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 p-6 sm:grid-cols-2 xl:grid-cols-3">
          {rates.map((rate) => {
            const service = serviceMeta[rate.nameService] || { label: rate.nameService };

            return (
                <div
                    key={rate.nameService}
                    className="relative min-w-0 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-green-cl/30 hover:shadow-md"
                >
                  <button
                      type="button"
                      className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-xl border border-red-100 bg-red-50 text-red-500 transition hover:bg-red-100 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-100"
                      onClick={() => handleDelete(rate)}
                      aria-label={`Apagar taxa ${service.label}`}
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>

                  <div className="px-5 py-6 pr-14">
                    <div className="flex min-w-0 items-center gap-4">
                      <ServiceLogo nameService={rate.nameService} />
                      <div className="min-w-0">
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-400">
                          Método de pagamento
                        </p>
                        <h3 className="mt-1 truncate text-lg font-extrabold text-gray-900">
                          {service.label}
                        </h3>
                      </div>
                    </div>

                    <div className="mt-5 text-sm font-bold text-gray-700">
                      {getRateTypeLabel(rate.rateType)}
                    </div>
                    <RateValue rate={rate} />
                  </div>
                </div>
            );
          })}
          {rates.length === 0 && <RateModal onRateSaved={handleRateSaved} />}
          {rates.length > 0 && <RateModalTax onRateSaved={handleRateSaved} />}
        </div>

        <ConfirmationModal
            open={showModal}
            rate={selectedRate}
            deleting={deleting}
            deleteError={deleteError}
            onDelete={handleConfirmDelete}
            onCancel={handleCancelDelete}
        />
      </div>
  );
};

export default NewRates;
